import { z } from 'zod'

/**
 * Common validation schemas used by both API and web
 */

// Pagination schema
export const paginationSchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
})

// Sort schema
export const sortSchema = z.object({
  sortBy: z.string().optional(),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
})

// Source schemas
export const createSourceSchema = z.object({
  title: z.string().min(1).max(500),
  contentType: z.string().min(1).max(100),
  rawContent: z.string().min(1),
  markdownContent: z.string().min(1),
  fileName: z.string().max(500).optional(),
  fileType: z.string().optional(),
  fileData: z.string().optional(),
  tags: z.array(z.string()).optional(),
  metadata: z.record(z.unknown()).optional(),
})

export const updateSourceSchema = z.object({
  title: z.string().min(1).max(500).optional(),
  contentType: z.string().min(1).max(100).optional(),
  tags: z.array(z.string()).optional(),
  metadata: z.record(z.unknown()).optional(),
})

// Facet schemas
export const createFacetSchema = z.object({
  facetType: z.string().min(1).max(100).optional(),
  facetTypeId: z.string().min(1).max(100).optional(),
  title: z.string().max(500).optional(),
  content: z.string().min(1),
  evidence: z.string().min(1),
  confidence: z.number().min(0).max(1),
  positionStart: z.number().int().optional(),
  positionEnd: z.number().int().optional(),
  problemContext: z.string().optional(),
  problemCause: z.string().optional(),
  problemConsequence: z.string().optional(),
  suggestedEntityType: z.string().optional(),
  suggestedReason: z.string().optional(),
}).refine((data) => data.facetType || data.facetTypeId, {
  message: 'Either facetType or facetTypeId is required',
})

export const updateFacetSchema = z.object({
  title: z.string().max(500).optional(),
  status: z.enum(['pending', 'accepted', 'rejected', 'mapped']).optional(),
})

// Project schemas
export const createProjectSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(2000).optional(),
})

export const updateProjectSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  description: z.string().max(2000).optional(),
  status: z.enum(['active', 'archived']).optional(),
})

// Workflow schemas
export const createWorkflowSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(2000).optional(),
  isActive: z.boolean().default(true),
})

// Prompt schemas
export const createPromptSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(2000).optional(),
  promptType: z.enum(['question', 'assess', 'followup', 'extract', 'guide']),
  systemPrompt: z.string().min(1),
  userTemplate: z.string().min(1),
  variables: z.array(z.object({
    name: z.string(),
    type: z.enum(['string', 'number', 'boolean', 'array', 'object']),
    description: z.string().optional(),
    required: z.boolean().optional(),
    defaultValue: z.unknown().optional(),
  })).optional(),
  outputFormat: z.enum(['text', 'json', 'markdown']).default('text'),
  maxTokens: z.number().int().positive().optional(),
  temperature: z.number().min(0).max(2).optional(),
})

// Type exports for use in API and web
export type CreateSourceInput = z.infer<typeof createSourceSchema>
export type UpdateSourceInput = z.infer<typeof updateSourceSchema>
export type CreateFacetInput = z.infer<typeof createFacetSchema>
export type UpdateFacetInput = z.infer<typeof updateFacetSchema>
export type CreateProjectInput = z.infer<typeof createProjectSchema>
export type UpdateProjectInput = z.infer<typeof updateProjectSchema>
export type CreateWorkflowInput = z.infer<typeof createWorkflowSchema>
export type CreatePromptInput = z.infer<typeof createPromptSchema>
